<template>
  <form class="d-grid gap-3 pb-3">
    <email-input :value="userData.email" :disabled="acceptingInvitation" required
                 entity-name="user" :validation-errors="validationErrors['email']"
                 @changed="(value) => fieldChanged('email', value)" />

    <unmaskable-password-field :validation-errors="validationErrors['password']" :value="userData.password"
                               @input="(value) => fieldChanged('password', value)" />
    <first-name-field :value="userData.firstName" entity-name="user" required :validation-errors="validationErrors['first_name']"
                      @changed="(value) => fieldChanged('firstName', value)" />
    <last-name-field :value="userData.lastName" entity-name="user" required :validation-errors="validationErrors['last_name']"
                     @changed="(value) => fieldChanged('lastName', value)" />
    <postcode-field v-if="askForPostcode" :label="postcodeLabel" :value="userData.postcode" required entity-name="user"
                    :validation-errors="validationErrors['postcode']"
                    @changed="(value) => fieldChanged('postcode', value)" />
    <phone-number-field :label="phoneNumberLabel" :value="userData.phoneNumber" entity-name="user"
                        :required="phoneNumberMandatory" :validation-errors="validationErrors['phone_number']"
                        @changed="(value) => fieldChanged('phoneNumber', value)" />

    <sms-opt-in-input v-if="askForSmsOptIn" :label="smsOptInLabel" :phone-number-present="!!userData.phoneNumber"
                      :sms-opt-in-type-data="smsOptInTypeData" :validation-errors="validationErrors['opt_in_sms']"
                      entity-name="user" @changed="updateSmsOptIn" />

    <country-select v-if="askForCountry" input-name="user[country]" input-id="user-country-select" :label="countryLabel"
                    :country-options="countryOptions" :geoip-hostname="geoipHostname"
                    :validation-errors="validationErrors['country']" :value="userData.country" required
                    @changed="(value) => fieldChanged('country', value)" />

    <additional-field v-for="config of additionalFieldConfigs" v-show="shouldShowAdditionalField(config)"
                      v-bind="additionalFieldConfig(config)" :key="config.name" entity-name="user"
                      :validation-errors="validationErrors[config.name]"
                      @changed="(value) => additionalFieldChanged(config.name, value)" />

    <email-opt-in-input :email-opt-in-type-data="emailOptInTypeData" entity-name="user" :label="emailOptInLabel"
                        :value="userData.joinOrganisation" :validation-errors="validationErrors['join_organisation']"
                        @changed="emailOptInChanged" />

    <accept-terms-of-service input-name="user[accept_tos]" :label="acceptTermsOfServiceLabel" :terms-of-service-explicit="termsOfServiceExplicit"
                             :value="userData.acceptTos" :validation-errors="validationErrors['accept_tos']"
                             @changed="(value) => fieldChanged('acceptTos', value)" />

    <eu-data-processing-consent v-if="includeEuDataProcessingConsent"
                                v-show="showEuDataProcessingConsent"
                                entity-name="user"
                                :explicit-consent-required="euDataProcessingConsentData.explicitConsentRequired"
                                :label="euDataProcessingConsentData.label"
                                :show-label="showDataProcessingConsentLabelOnForm"
                                input-name="user[eu_data_processing_consent]"
                                :value="userData.euDataProcessingConsent"
                                :validation-errors="validationErrors['eu_data_processing_consent?']"
                                @changed="(value) => fieldChanged('euDataProcessingConsent', value)" />

    <div class="mt-1 small-text" v-html="newAccountDisclaimer" />

    <bot-protected-submit-button :label="submitButtonText" captcha-action="user_create" button-class="w-100"
                                 :validation-errors="validationErrorsCamelCase" @submit="submit" />

    <div v-if="showDataProcessingConsentAfterSubmit" class="small-text mt-2"
         v-html="euDataProcessingConsentData.label" />

    <bot-protection-disclaimer v-if="botProtectionEnabled" />
  </form>
</template>

<script>
import ajax from '@/helpers/ajax'
import { objectKeysToCamelCase, objectKeysToSnakeCase } from '@/helpers/transform-object-keys'
import AcceptTermsOfService from '@/components/inputs/accept-terms-of-service'
import AdditionalField from '@/components/inputs/additional-field'
import BotProtectedSubmitButton from '@/components/buttons/bot-protected-submit-button'
import BotProtectionDisclaimer from '@/components/inputs/bot-protection-disclaimer'
import CountrySelect from '@/components/inputs/country-select'
import EmailInput from '@/components/inputs/email-input-with-mailcheck'
import EmailOptInInput from '@/components/inputs/email-opt-in-input'
import { EU_COUNTRIES } from '@/eu-countries'
import EuDataProcessingConsent from '@/components/inputs/eu-data-processing-consent.vue'
import FirstNameField from '@/components/inputs/first-name-field'
import HasAdditionalFields from '@/mixins/has-additional-fields'
import { inject } from 'vue'
import LastNameField from '@/components/inputs/last-name-field'
import PhoneNumberField from '@/components/inputs/phone-number-field'
import PostcodeField from '@/components/inputs/postcode-field'
import SmsOptInInput from '@/components/inputs/sms-opt-in-input'
import UnmaskablePasswordField from '@/components/inputs/unmaskable-password-field.vue'

export default {
  components: { AcceptTermsOfService, AdditionalField, BotProtectedSubmitButton, BotProtectionDisclaimer, CountrySelect,
                EmailInput, EmailOptInInput, EuDataProcessingConsent, FirstNameField, LastNameField, PhoneNumberField,
                PostcodeField, SmsOptInInput, UnmaskablePasswordField },

  mixins: [ HasAdditionalFields ],

  props: {
    acceptTermsOfServiceLabel: {
      type: String,
      required: true
    },

    additionalFieldConfigs: {
      type: Array,
      required: true
    },

    askForCountry: {
      type: Boolean,
      required: true
    },

    askForPostcode: {
      type: Boolean,
      required: true
    },

    askForSmsOptIn: {
      type: Boolean,
      required: true
    },

    countryLabel: {
      type: String,
      required: false
    },

    countryOptions: {
      type: Array,
      required: false
    },

    countryTogglesEuDataProcessingConsent: {
      type: Boolean,
      required: false
    },

    emailOptInTypeData: {
      type: Object,
      required: true
    },

    emailOptInLabel: {
      type: String,
      required: true
    },

    euDataProcessingConsentData: {
      type: Object,
      required: false
    },

    includeEuDataProcessingConsent: {
      type: Boolean,
      required: true
    },

    invitation: {
      type: Object,
      required: false
    },

    newAccountDisclaimer: {
      type: String,
      required: false
    },

    phoneNumberLabel: {
      type: String,
      required: false,
      default() { return inject('i18n').t('simple_form.labels.defaults.phone_number') }
    },

    phoneNumberMandatory: {
      type: Boolean,
      required: true
    },

    postcodeLabel: {
      type: String,
      required: false,
      default() { return inject('i18n').t('activerecord.attributes.user.postcode') }
    },

    signUpPath: {
      type: String,
      required: true
    },

    smsOptInLabel: {
      type: String,
      required: false
    },

    smsOptInTypeData: {
      type: Object,
      required: false
    },

    submitButtonText: {
      type: String,
      required: true
    },

    termsOfServiceExplicit: {
      type: Boolean,
      required: true
    }
  },

  emits: ['authenticated', 'awaitingEmailConfirmation', 'errorOnSignUp', 'mfa-setup-required'],

  data() {
    const userData = {
      acceptTos: null,
      country: null,
      email: this.invitation ? this.invitation.email : null,
      emailOptInTypeId: null,
      euDataProcessingConsent: null,
      firstName: null,
      joinOrganisation: null,
      lastName: null,
      optInSms: null,
      password: null,
      phoneNumber: null,
      postcode: null,
      smsOptInTypeId: null
    }

    if (this.additionalFieldConfigs.length > 0) {
      userData.additionalFields = {}
    }

    return {
      userData,
      validationErrors: {}
    }
  },

  computed: {
    acceptingInvitation() {
      return !!this.invitation
    },

    botProtectionEnabled() {
      return this.agraConfiguration.botProtection.kind !== 'none'
    },

    geoipHostname() {
      return this.agraConfiguration.geoipHostname
    },

    showDataProcessingConsentAfterSubmit() {
      if (!this.showEuDataProcessingConsent) {
        return false
      }

      if (this.euDataProcessingConsentData.explicitConsentRequired) {
        return false
      } else {
        return this.euDataProcessingConsentData.messagePosition === 'after_submit'
      }
    },

    showDataProcessingConsentLabelOnForm() {
      if (this.euDataProcessingConsentData.explicitConsentRequired) {
        return true
      } else {
        return this.euDataProcessingConsentData.messagePosition === 'before_submit'
      }
    },

    showEuDataProcessingConsent() {
      if (this.countryTogglesEuDataProcessingConsent) {
        return EU_COUNTRIES.includes(this.userData.country)
      } else {
        return this.includeEuDataProcessingConsent
      }
    },

    validationErrorsCamelCase() {
      return objectKeysToCamelCase(this.validationErrors)
    }
  },

  methods: {
    additionalFieldConfig(config) {
      return {
        initialValue: this.additionalFieldValue(config.name),
        fieldName: config.name,
        ...config.validations,
        ...config
      }
    },

    additionalFieldChanged(fieldName, value) {
      this.userData.additionalFields[fieldName] = value
    },

    additionalFieldValue(fieldName) {
      return this.userData.additionalFields[fieldName]
    },

    emailOptInChanged(value) {
      this.userData['emailOptInTypeId'] = value.emailOptInTypeId
      this.userData['joinOrganisation'] = value.joinOrganisation
    },

    fieldChanged(fieldName, value) {
      this.userData[fieldName] = value
    },

    submit({ friendlyCaptchaSolution, hcaptchaResponse, recaptchaToken, turnstileToken }) {
      const data = { user: objectKeysToSnakeCase(this.userData) }
      if (this.acceptingInvitation) {
        data.invitation_token = this.invitation.token
      }
      if (friendlyCaptchaSolution) {
        data.friendly_captcha_solution = friendlyCaptchaSolution
      }
      if (hcaptchaResponse) {
        data.hcaptcha_response = hcaptchaResponse
      }
      if (recaptchaToken) {
        data.recaptcha_token = recaptchaToken
      }
      if (turnstileToken) {
        data.turnstile_token = turnstileToken
      }

      ajax.post({
        url: this.signUpPath,
        data,
        camelCaseResponse: false,
        successHandler: (response) => {
          if (response.success) {
            if (response.requiresEmailConfirmation) {
              this.$emit('awaitingEmailConfirmation', { userEmail: this.userData.email, userToken: response.userToken })
            }
            else if (response.requiresMfaSetup) {
              this.$emit('mfa-setup-required', {
                enableMfaPath: response.enableMfaPath
              })
            }
            else {
              this.$emit('authenticated', {
                afterSignInRedirect: response.afterSignInRedirect,
                userMenu: response.userMenu
              })
            }
          } else {
            // Handle non recoverable errors (i.e.: banned member causes new user account to be disabled)
            this.$emit('errorOnSignUp', { errorMessage: response.errorMessage })
          }
        },
        errorHandler: ({ info }) => {
          this.validationErrors = info.errors
        }
      })
    },

    updateSmsOptIn(value) {
      this.userData.optInSms = value.optInSms
      this.userData.smsOptInTypeId = value.smsOptInTypeId
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;

// TODO: DRY with sign-in-form
::v-deep(input:not([type=checkbox],[type=radio])),
::v-deep(select) {
  height: 58px;

  @include breakpoints.screen-mobile-only {
    height: 50px;
  }

}

.btn-primary {
  text-transform: none;
  height: 56px;
  border-radius: 8px;

  @include breakpoints.screen-mobile-only {
    height: 50px;
  }
}
</style>

